/**
 * Optimisateur SEO amélioré avec WebP, Schema markup et meta descriptions avancées
 */

export interface EnhancedSEOData {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogImage: string;
  schema: any;
  breadcrumbs?: BreadcrumbItem[];
  imageOptimization?: ImageSEOData;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface ImageSEOData {
  images: Array<{
    url: string;
    alt: string;
    width?: number;
    height?: number;
    format?: 'webp' | 'jpg' | 'png';
  }>;
  lazyLoad: boolean;
  webpSupport: boolean;
}

export class EnhancedSEOOptimizer {
  private static readonly BASE_URL = 'https://chateaudeclementine.com';
  
  /**
   * Génère des meta descriptions optimisées pour différents types de contenu
   */
  static generateEnhancedMetaDescription(
    type: 'chateau' | 'home' | 'listing' | 'blog' | 'contact',
    data: any
  ): string {
    const templates = {
      chateau: (d: any) => 
        `${d.nom} - ${d.description} Taille: ${d.taille}, Capacité: ${d.capacite}, Prix: ${d.prix}/weekend. Location à Rhode-Saint-Genèse, Belgique. Montage 10min.`,
      
      home: () => 
        'Location châteaux gonflables & sumos à Rhode-Saint-Genèse dès 60€/weekend. Retrait facile, montage 10min. 30+ ans d\'expérience. Anniversaires réussis en Belgique.',
      
      listing: (d: any) => 
        `Découvrez ${d.count || 'notre collection de'} châteaux gonflables à louer. Prix dès 60€/weekend complet. Transport facile, montage rapide. Plus de 30 ans d'expérience.`,
      
      blog: (d: any) => 
        `${d.excerpt || d.description} Conseils d'experts avec 30+ ans d'expérience dans l'événementiel. Lisez nos guides pour organiser des fêtes inoubliables.`,
      
      contact: () => 
        'Contactez Château de Clémentine pour louer votre château gonflable. Devis gratuit, conseils personnalisés, service rapide. Plus de 30 ans d\'expérience à votre service.'
    };

    const description = templates[type](data);
    return description.length > 160 ? description.substring(0, 157) + '...' : description;
  }

  /**
   * Génère du schema markup pour les châteaux
   */
  static generateChateauSchema(chateau: any): any {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": chateau.nom,
      "description": chateau.description,
      "image": chateau.imageUrls || [],
      "brand": {
        "@type": "Brand",
        "name": "Château de Clémentine"
      },
      "offers": {
        "@type": "Offer",
        "price": parseInt(chateau.prix.replace(/[^\d]/g, '')) || 60,
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": parseInt(chateau.prix.replace(/[^\d]/g, '')) || 60,
          "priceCurrency": "EUR",
          "unitText": "weekend"
        }
      },
      "category": "Château Gonflable",
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Taille",
          "value": chateau.taille
        },
        {
          "@type": "PropertyValue", 
          "name": "Capacité",
          "value": chateau.capacite
        },
        {
          "@type": "PropertyValue",
          "name": "Âge recommandé", 
          "value": chateau.age_recommande || chateau.ageRecommande
        }
      ],
      "audience": {
        "@type": "PeopleAudience",
        "suggestedMinAge": 3,
        "suggestedMaxAge": 14
      }
    };
  }

  /**
   * Génère du schema markup pour l'organisation
   */
  static generateOrganizationSchema(): any {
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${this.BASE_URL}/#organization`,
      "name": "Château de Clémentine",
      "alternateName": "Location Châteaux Gonflables Rhode-Saint-Genèse",
      "description": "Location de châteaux gonflables et sumos pour anniversaires et événements. Plus de 30 ans d'expérience dans l'événementiel.",
      "url": this.BASE_URL,
      "logo": `${this.BASE_URL}/lovable-uploads/logo.webp`,
      "image": `${this.BASE_URL}/lovable-uploads/logo.webp`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Rhode-Saint-Genèse",
        "addressRegion": "Brabant Wallon",
        "addressCountry": "BE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 50.7439,
        "longitude": 4.3538
      },
      "areaServed": [
        "Rhode-Saint-Genèse",
        "Bruxelles",
        "Brabant Wallon",
        "Namur",
        "Charleroi"
      ],
      "serviceType": "Location châteaux gonflables",
      "priceRange": "€60-€120",
      "foundingDate": "1990",
      "sameAs": []
    };
  }

  /**
   * Génère du schema markup pour les breadcrumbs
   */
  static generateBreadcrumbSchema(items: BreadcrumbItem[]): any {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url.startsWith('http') ? item.url : `${this.BASE_URL}${item.url}`
      }))
    };
  }

  /**
   * Génère du schema markup pour les FAQ
   */
  static generateFAQSchema(faqs: Array<{ question: string; answer: string }>): any {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  }

  /**
   * Optimise les URLs d'images pour WebP
   */
  static optimizeImageUrls(
    images: string[], 
    options: { width?: number; quality?: number; format?: 'webp' | 'auto' } = {}
  ): ImageSEOData {
    const { width = 800, quality = 80, format = 'auto' } = options;
    
    const optimizedImages = images.map(url => {
      if (url.includes('placeholder')) {
        return {
          url,
          alt: 'Image de château gonflable',
          format: 'png' as const
        };
      }

      // Ajouter les paramètres d'optimisation
      const separator = url.includes('?') ? '&' : '?';
      const optimizedUrl = `${url}${separator}w=${width}&q=${quality}&format=webp`;
      
      return {
        url: optimizedUrl,
        alt: 'Château gonflable de location',
        width,
        height: Math.round(width * 0.75), // Aspect ratio 4:3
        format: 'webp' as const
      };
    });

    return {
      images: optimizedImages,
      lazyLoad: true,
      webpSupport: true
    };
  }

  /**
   * Génère des mots-clés SEO avancés
   */
  static generateAdvancedKeywords(
    baseKeywords: string[],
    location: string = 'Rhode-Saint-Genèse',
    category: string = 'château gonflable'
  ): string[] {
    const locationKeywords = [
      location,
      'Belgique',
      'Bruxelles',
      'Brabant Wallon',
      'proche Bruxelles'
    ];

    const actionKeywords = [
      'location',
      'louer',
      'réserver',
      'anniversaire',
      'fête enfant',
      'événement'
    ];

    const qualityKeywords = [
      'professionnel',
      'sécurisé', 
      'qualité',
      '30 ans expérience',
      'montage facile',
      'transport inclus'
    ];

    return [
      ...baseKeywords,
      ...baseKeywords.map(kw => `${kw} ${location}`),
      ...actionKeywords.map(kw => `${kw} ${category}`),
      ...locationKeywords.map(kw => `${category} ${kw}`),
      ...qualityKeywords
    ].slice(0, 15); // Limiter à 15 mots-clés
  }

  /**
   * Génère des données SEO complètes pour une page
   */
  static generatePageSEO(
    type: 'chateau' | 'home' | 'listing' | 'blog' | 'contact',
    data: any = {}
  ): EnhancedSEOData {
    const baseKeywords = data.keywords || [];
    const enhancedKeywords = this.generateAdvancedKeywords(baseKeywords);
    
    let schema: any;
    switch (type) {
      case 'chateau':
        schema = this.generateChateauSchema(data);
        break;
      case 'home':
        schema = this.generateOrganizationSchema();
        break;
      default:
        schema = this.generateOrganizationSchema();
    }

    return {
      title: data.title || 'Château de Clémentine',
      description: this.generateEnhancedMetaDescription(type, data),
      keywords: enhancedKeywords,
      canonicalUrl: data.canonicalUrl || this.BASE_URL,
      ogImage: data.ogImage || `${this.BASE_URL}/lovable-uploads/logo.webp`,
      schema,
      breadcrumbs: data.breadcrumbs,
      imageOptimization: data.images ? this.optimizeImageUrls(data.images) : undefined
    };
  }
}